import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Play, Pause, SkipForward, SkipBack, ChevronUp, X, Music2, ExternalLink } from "lucide-react";
import { api, asset } from "@/lib/api";
import { usePlayer, isPreview, isPlayable } from "./usePlayer";
import { fmt, SeekBar, VolumeControl, MuteBtn, SectionPrompt, SECTION_LABEL } from "./controls";
import { useSectionSoundtracks } from "./useSectionSoundtracks";

const PreviewTag = ({ testid }) => (
  <span data-testid={testid} className="shrink-0 border border-[#F5A524]/60 text-[#F5A524] font-mono text-[9px] font-bold tracking-[0.2em] px-1.5 py-0.5">PREVIEW</span>
);

const EMPTY = [];
const NONE = {};

const Bars = ({ on }) => (
  <span className="inline-flex items-end gap-[2px] h-3" aria-hidden="true">
    {[0, 1, 2, 3].map((k) => (
      <span key={k} className={`w-[3px] bg-[#F5A524] ${on ? "eq-bar" : ""}`} style={{ height: on ? undefined : 3, animationDelay: `${k * 0.15}s` }} />
    ))}
  </span>
);

const Btn = ({ testid, label, onClick, big, children }) => (
  <button data-testid={testid} aria-label={label} onClick={onClick}
    className={`grid place-items-center transition-[background-color,color,transform] active:scale-90 ${big ? "w-12 h-12 bg-[#F5A524] text-black hover:bg-white" : "w-9 h-9 text-[#F5A524] hover:bg-[#F5A524]/15"}`}>
    {children}
  </button>
);

const Panel = ({ data, s, onCollapse }) => {
  const t = s.cur;
  const playing = s.playing;
  return (
    <motion.div data-testid="music-panel" initial={{ opacity: 0, y: 20, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className="clip-corner bg-[#F5A524]/50 p-px w-[calc(100vw-24px)] sm:w-[360px]">
      <div className="clip-corner bg-[#0B0C10]/95 backdrop-blur-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#F5A524]/20 font-mono text-[11px] tracking-[0.25em]">
          <span data-testid="music-panel-heading" className="text-[#F5A524] truncate">// {s.section ? `${SECTION_LABEL[s.section].toUpperCase()} SOUNDTRACK` : `SOUNDTRACK — ${data.name}`}</span>
          <button data-testid="music-collapse-btn" onClick={onCollapse} aria-label="Collapse player" className="text-zinc-400 hover:text-[#F5A524]"><ChevronUp size={16} className="rotate-180" /></button>
        </div>
        <div className="p-4 flex gap-4 items-center">
          <div className="relative w-24 h-24 shrink-0 clip-corner-sm overflow-hidden scanlines">
            <AnimatePresence initial={false}>
              <motion.img key={t?.cover} src={asset(t?.cover || data.cover)} alt="" initial={{ opacity: 0, scale: 1.1 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }} className="absolute inset-0 w-full h-full object-cover" />
            </AnimatePresence>
          </div>
          <div className="min-w-0">
            <p className="font-mono text-[10px] tracking-[0.3em] text-[#F5A524] flex items-center gap-2"><Bars on={playing} /> NOW PLAYING</p>
            <p data-testid="music-now-title" className="mt-1 font-display font-black uppercase text-2xl leading-none truncate">{t?.title}</p>
            <p data-testid="music-now-artist" className="mt-1 text-sm text-zinc-400 truncate">{t?.artist}</p>
            {isPreview(t) && <div className="mt-2"><PreviewTag testid="music-now-preview-tag" /></div>}
          </div>
        </div>
        <div className="px-4">
          <SeekBar s={s} fallbackMs={t?.duration_ms} />
        </div>
        <div className="flex items-center justify-center gap-3 py-2">
          <Btn testid="music-prev-btn" label="Previous track" onClick={s.prev}><SkipBack size={18} /></Btn>
          <Btn testid="music-play-btn" label={playing ? "Pause" : "Play"} onClick={s.toggle} big>{playing ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}</Btn>
          <Btn testid="music-next-btn" label="Next track" onClick={s.next}><SkipForward size={18} /></Btn>
        </div>
        <VolumeControl s={s} />
        <div data-lenis-prevent className="max-h-[32vh] overflow-y-auto border-t border-[#F5A524]/20">
          {data.tracks.map((tr, k) => (
            <button key={tr.id || k} data-testid={`music-track-${k}`} onClick={() => s.play(k)}
              className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors ${k === s.idx && !s.section ? "bg-[#F5A524]/10" : "hover:bg-white/5"}`}>
              <img src={asset(tr.cover)} alt="" loading="lazy" className="w-9 h-9 object-cover shrink-0" />
              <span className="min-w-0 flex-1">
                <span className={`block text-sm truncate ${k === s.idx ? "text-[#F5A524]" : "text-zinc-200"}`}>{tr.title}</span>
                <span className="block text-xs text-zinc-400 truncate">{tr.artist}</span>
              </span>
              {isPreview(tr) && <PreviewTag testid={`music-track-preview-${k}`} />}
              {k === s.idx && !s.section && playing ? <Bars on /> : <span className="font-mono text-[10px] text-zinc-400">{fmt(tr.duration_ms)}</span>}
            </button>
          ))}
        </div>
        {(data.tracks.some(isPreview) || data.url) && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-[#F5A524]/20">
            <span className="text-[11px] text-zinc-400 leading-snug">{data.tracks.some(isPreview) ? "Tracks tagged PREVIEW play a 30s clip." : ""}</span>
            {data.url && <a data-testid="music-open-source" href={data.url} target="_blank" rel="noreferrer" className="shrink-0 flex items-center gap-1 font-mono text-[10px] tracking-[0.2em] text-[#F5A524] hover:text-white">FULL PLAYLIST <ExternalLink size={11} /></a>}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const Mini = ({ data, s, onExpand, onHide }) => {
  const t = s.cur;
  const playing = s.playing;
  return (
    <motion.div data-testid="music-mini" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
      className="clip-tab bg-[#0B0C10]/90 backdrop-blur-xl border border-[#F5A524]/50 flex items-center gap-3 pr-2 w-[min(320px,calc(100vw-24px))]">
      <button data-testid="music-expand-btn" onClick={onExpand} aria-label="Open player" className="relative w-14 h-14 shrink-0 overflow-hidden">
        <img src={asset(t?.cover || data.cover)} alt="" className={`w-full h-full object-cover ${playing ? "spin-slow rounded-full scale-90" : ""} transition-[border-radius,transform] duration-500`} />
      </button>
      <button onClick={onExpand} className="min-w-0 flex-1 text-left">
        <span className="font-mono text-[10px] tracking-[0.25em] text-[#F5A524] flex items-center gap-2"><Bars on={playing} /> {playing ? "NOW PLAYING" : "TAP PLAY"}</span>
        <span className="block text-sm text-white truncate">{t?.title}</span>
        <span className="block text-xs text-zinc-400 truncate">{t?.artist}</span>
      </button>
      <Btn testid="music-mini-play-btn" label={playing ? "Pause" : "Play"} onClick={s.toggle}>{playing ? <Pause size={18} /> : <Play size={18} />}</Btn>
      <MuteBtn s={s} testid="music-mini-mute-btn" />
      <Btn testid="music-hide-btn" label="Hide player" onClick={onHide}><X size={16} /></Btn>
    </motion.div>
  );
};

export const MusicPlayer = () => {
  const { pathname } = useLocation();
  const [data, setData] = useState(null);
  const [mode, setMode] = useState(() => localStorage.getItem("emporio-music") || "mini");
  const tracks = data?.tracks ?? EMPTY;
  const sections = data?.sections ?? NONE;
  const s = usePlayer(tracks, sections);
  const st = useSectionSoundtracks(s, sections, !!data && pathname === "/");

  useEffect(() => {
    api.get("/music").then((r) => {
      const list = Array.isArray(r.data?.tracks) ? r.data.tracks.filter(isPlayable) : [];
      const secs = Object.fromEntries(Object.entries(r.data?.sections || {}).filter(([, t]) => isPlayable(t)));
      setData(r.data?.enabled && list.length ? { ...r.data, tracks: list, sections: secs } : null);
    }).catch(() => setData(null));
  }, []);

  useEffect(() => {
    localStorage.setItem("emporio-music", mode === "open" ? "mini" : mode);
  }, [mode]);

  const show = data && tracks.length > 0 && !pathname.startsWith("/admin");

  return (
    <>
      <div ref={s.ytHost} aria-hidden="true" style={{ position: "fixed", left: -10000, top: 0, width: 320, height: 180 }} />
      {show && (
      <div className="fixed bottom-3 left-3 z-40" data-testid="music-player">
        <AnimatePresence>
          {st.prompt && mode !== "open" && (
            <SectionPrompt key={st.prompt} name={st.prompt} track={sections[st.prompt]} onPlay={() => s.playSection(st.prompt)} onDismiss={st.dismiss} />
          )}
        </AnimatePresence>
        <AnimatePresence mode="popLayout">
          {mode === "open" && <Panel key="p" data={data} s={s} onCollapse={() => setMode("mini")} />}
          {mode === "mini" && <Mini key="m" data={data} s={s} onExpand={() => setMode("open")} onHide={() => setMode("hidden")} />}
          {mode === "hidden" && (
            <motion.button key="h" data-testid="music-show-btn" onClick={() => setMode("mini")} aria-label="Show music player"
              initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
              className="w-12 h-12 rounded-full bg-[#F5A524] text-black grid place-items-center shadow-[0_0_30px_rgba(245,165,36,.4)] hover:scale-110 transition-transform">
              <Music2 size={20} />
            </motion.button>
          )}
        </AnimatePresence>
      </div>
      )}
    </>
  );
};
